import { registerLocaleData } from '@angular/common';

import { HttpClient, HttpClientModule } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import '@angular/compiler';
import { environment } from '@environments/environment';
import { EffectsModule } from '@ngrx/effects';

import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
// I18n
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { DialogModule, WindowModule } from '@progress/kendo-angular-dialog';
// Kendo UI
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IntlModule } from '@progress/kendo-angular-intl';
import '@progress/kendo-angular-intl/locales/de/all';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { AuthorizationModule } from 'app/core/authorization/authorization.module';
import { InterceptorsForRootModule } from 'app/core/interceptors/interceptors-for-root.module';
import { NavbarModule } from 'app/core/layout/navbar/navbar.module';
import { API_BASE_URL } from 'app/core/services/api-client';
import { ApiClientModule } from 'app/core/services/api-client/api-client.module';
import { CoreServicesForRootModule } from 'app/core/services/core-services.module';
import { ChemSketchImportModule } from 'app/modules/dossier-area/entry-components/chemsketch-import/chemsketch-import.module';
import { UserEffects } from 'app/modules/shared-area/ducks/user/store/user.effects';
import { SachverhaltCreateModule } from 'app/modules/shared-area/entry-components/sachverhalt/sachverhalt-create/sachverhalt-create.module';
import { SachverhaltEditModule } from 'app/modules/shared-area/entry-components/sachverhalt/sachverhalt-edit/sachverhalt-edit.module';
import { REDUCER_TOKEN, reducerProvider } from 'app/modules/shared-area/root-state';
// Helpers
import { SharedAreaModule } from 'app/modules/shared-area/shared-area.module';
import { RangeFieldModule } from 'app/shared/components/range-field/range-field.module';
import { HasrightModule } from 'app/shared/directives/hasright/hasright.module';
import { NotHasRightModule } from 'app/shared/directives/nothasright/nothasright.module';
import { IsPanelVisibleModule } from 'app/shared/directives/is-panel-visible/is-panel-visible.module';
import { ToastModule } from 'app/core/services/toast-service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SelectMerkmalDialogModule } from './modules/shared-area/entry-components/select-merkmal-dialog/select-merkmal-dialog.module';
import { StoffFeedbackModule } from 'app/modules/feedback/components/stoff-feedback/stoff-feedback.module';
import { CommonFeedbackModule } from 'app/modules/feedback/components/common-feedback/common-feedback.module';
import { ReferencesDialogModule } from './modules/dossier-area/components/references/references-dialog.module';
import { SystemInfoServiceModule } from './core/services/system-info/system-info-service.module';
import { WindowEffects } from './modules/shared-area/ducks/window/store';
import { MergeDialogModule } from './modules/dossier-area/components/merge/merge-dialog.module';
import { DynamicLocaleId } from 'app/core/services/i18n/dynamicLocaleId';
import { I18nService } from 'app/core/services/i18n/i18n.service';
import { CustomTranslateLoader } from 'app/core/translation/custom-translate-loader';
import { CopySachverhalteModule } from 'app/modules/dossier-area/entry-components/copy-sachverhalte/copy-sachverhalte.module';
import { SystemInfoEffects } from './modules/shared-area/ducks/system-info/store';
import { AuthModule, LogLevel } from 'angular-auth-oidc-client';
import { AssignToModule } from './modules/home-area/components/assign-to/assign-to.module';
import { GenericTableConfigProvider } from './shared/components/generic-table/generic-table-translation.service';

// Declare global window interface for appSettings
declare global {
  interface Window {
    appSettings: any;
  }
}

// Interceptors

registerLocaleData(localeDe, localeDeExtra);
registerLocaleData(localeEn, localeEnExtra);

const effects = [
  EffectsModule.forRoot([
    SystemInfoEffects,
    UserEffects,
    WindowEffects,
  ]),
];

@NgModule({
    declarations: [AppComponent],
    imports: [
        ChemSketchImportModule,
        CopySachverhalteModule,
        SachverhaltCreateModule,
        SachverhaltEditModule,
        StoffFeedbackModule,
        SelectMerkmalDialogModule,
        ReferencesDialogModule,
        MergeDialogModule,
        CommonFeedbackModule,
        AssignToModule,        
        RangeFieldModule,
        SharedAreaModule,
        ApiClientModule.forRoot(),
        AuthorizationModule.forRoot(),
        ToastModule.forRoot(),
        AuthModule.forRoot({
            config: {
                authority: window.appSettings.authentication.stsServer,
                redirectUrl: window.appSettings.authentication.redirect_url,
                clientId: 'chemInfoSpaClient',
                scope: 'openid cheminfo.api cheminfo.profile',
                postLogoutRedirectUri: window.appSettings.authentication.post_logout_redirect_uri,
                startCheckSession: false,
                responseType: 'id_token token',
                forbiddenRoute: '/forbidden',
                unauthorizedRoute: '/unauthorized',
                logLevel: window.appSettings.authentication.log_console_debug_active
                    ? LogLevel.Debug
                    : window.appSettings.authentication.log_console_warning_active
                        ? LogLevel.Warn
                        : LogLevel.Error,
                silentRenew: true,
                renewTimeBeforeTokenExpiresInSeconds: 30,
                silentRenewUrl: window.appSettings.authentication.redirect_url + (window.appSettings.authentication.redirect_url.endsWith('/') ? '' : '/') + 'assets/silent_renew.html',
                maxIdTokenIatOffsetAllowedInSeconds: 60,
                triggerAuthorizationResultEvent: true,
                postLoginRoute: '/',
            }
        }),
        SystemInfoServiceModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: AppModule.GetTranslationLoader,
                deps: [HttpClient],
            },
        }),
        StoreModule.forRoot(REDUCER_TOKEN, {
            runtimeChecks: {
                strictStateSerializability: false,
                strictActionSerializability: false,
                strictStateImmutability: false,
                strictActionImmutability: false,
                strictActionWithinNgZone: false,
            },
        }),
        CoreServicesForRootModule,
        InterceptorsForRootModule,
        ...effects,
        NavbarModule,
        IsPanelVisibleModule,
        HasrightModule,
        NotHasRightModule,
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({
          timeOut: 5000,
          positionClass: 'toast-top-right',
          preventDuplicates: true,
          enableHtml: true,
          closeButton: true,
          progressBar: true,
          tapToDismiss: true,
          maxOpened: 5,
          autoDismiss: true
        }),
        DatePickerModule,
        InputsModule,
        WindowModule,
        DialogModule,
        ButtonsModule,
        TooltipModule,
        TreeViewModule,
        IntlModule,
        /**
         * Store devtools instrument the store retaining past versions of state
         * and recalculating new states. This enables powerful time-travel
         * debugging.
         *
         * To use the debugger, install the Redux Devtools extension for either
         * Chrome or Firefox
         *
         * See: https://github.com/zalmoxisus/redux-devtools-extension
         */
        StoreDevtoolsModule.instrument({
            name: 'Comparex ChemInfo',
            logOnly: environment.production,
        }),
    ],
    providers: [
        reducerProvider,
        {
            provide: LOCALE_ID,
            useClass: DynamicLocaleId,
            deps: [I18nService],
        },
        Title,
        {
            provide: API_BASE_URL,
            useFactory: AppModule.apiClientUrlFactory,
        },
        GenericTableConfigProvider.forRoot({})
    ],
    bootstrap: [AppComponent]
})
export class AppModule {

  public static GetTranslationLoader(http: HttpClient): TranslateLoader {
    return new CustomTranslateLoader(http);
  }

  public static apiClientUrlFactory(): string {
    return environment.baseAPIUrl;
  }
}
